// Shared compliance status contract for the new-comparability workflow.
//
// Every screen the workflow surfaces (gateway, coverage, rate groups, ABPT,
// §415(c), §404 deduction, top-heavy) reports through this one vocabulary so
// the UI, Apply action, and exports never have to reinterpret ad-hoc booleans:
//
//   passes          — every in-scope required calculation passed and no
//                     blocking or review-only fact was used.
//   fails           — complete in-scope inputs produced a mathematical failure.
//   requires-review — the math may pass, but at least one professional /
//                     document / facts-and-circumstances decision remains.
//   unavailable     — a required calculation cannot run from the supplied data.
//   not-applicable  — the screen does not apply to this design.
//
// Low-level math modules keep their internal booleans; this contract wraps
// them at the workflow boundary. Nothing here is a formal nondiscrimination
// determination — 'passes' always means "modeled result on disclosed
// assumptions", never a legal opinion.

export const COMPLIANCE_STATUSES = Object.freeze([
  'passes', 'fails', 'requires-review', 'unavailable', 'not-applicable',
])

// Bumped whenever a regulatory calculation changes in a way that can move a
// result; persisted with proposals so an old saved proposal is never confused
// with the current engine's output.
export const NEW_COMP_CALCULATION_VERSION = 'new-comp-2026.07.3'

// Combination severity: a missing required input dominates (the overall
// determination cannot be made), then a mathematical failure, then an open
// professional decision. 'not-applicable' is neutral.
const SEVERITY = Object.freeze({
  'not-applicable': 0,
  passes: 1,
  'requires-review': 2,
  fails: 3,
  unavailable: 4,
})

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

export const isComplianceStatus = (value) => COMPLIANCE_STATUSES.includes(value)

export function makeReason(code, message, { employeeIds, field } = {}) {
  return {
    code: String(code),
    message: String(message),
    ...(Array.isArray(employeeIds) && employeeIds.length ? { employeeIds: [...employeeIds] } : {}),
    field: field ?? null,
  }
}

export function makeAssumption(code, label, value, { source = 'modeled', estimated = false } = {}) {
  return { code: String(code), label: String(label), value, source, estimated: estimated === true }
}

export function makeLimitation(code, message, severity = 'review') {
  return {
    code: String(code),
    message: String(message),
    severity: severity === 'blocking' ? 'blocking' : 'review',
  }
}

export function makeComplianceStatus({
  status,
  modeled = true,
  reasons = [],
  assumptions = [],
  limitations = [],
  calculationVersion = NEW_COMP_CALCULATION_VERSION,
  rulesYear = null,
  inputFingerprint = null,
} = {}) {
  return {
    status: isComplianceStatus(status) ? status : 'unavailable',
    modeled: modeled !== false,
    reasons: asArray(reasons),
    assumptions: asArray(assumptions),
    limitations: asArray(limitations),
    calculationVersion,
    rulesYear,
    inputFingerprint,
  }
}

// Combine part statuses (strings or status objects) into one workflow status.
// Reasons/assumptions/limitations merge and de-duplicate by code+message so a
// shared limitation surfaced by three screens reads once.
export function combineComplianceStatuses(parts = [], overrides = {}) {
  const statuses = []
  const reasons = []
  const assumptions = []
  const limitations = []
  const seen = new Set()
  const push = (list, entry, key) => {
    if (!entry || seen.has(key)) return
    seen.add(key)
    list.push(entry)
  }
  for (const part of asArray(parts)) {
    const status = typeof part === 'string' ? part : part.status
    if (isComplianceStatus(status)) statuses.push(status)
    if (typeof part === 'object') {
      for (const reason of asArray(part.reasons)) push(reasons, reason, `r:${reason.code}:${reason.message}`)
      for (const assumption of asArray(part.assumptions)) push(assumptions, assumption, `a:${assumption.code}:${assumption.label}`)
      for (const limitation of asArray(part.limitations)) push(limitations, limitation, `l:${limitation.code}:${limitation.message}`)
    }
  }
  const combined = statuses.reduce(
    (worst, status) => (SEVERITY[status] > SEVERITY[worst] ? status : worst),
    'not-applicable',
  )
  return makeComplianceStatus({
    status: statuses.length ? combined : 'unavailable',
    reasons,
    assumptions,
    limitations,
    ...overrides,
  })
}

// Apply gating (workflow contract): 'passes' applies directly;
// 'requires-review' applies only with an explicit recorded acknowledgment;
// 'fails' / 'unavailable' / 'not-applicable' never apply.
export function canApplyComplianceStatus(status, { acknowledged = false } = {}) {
  const value = typeof status === 'string' ? status : status?.status
  if (value === 'passes') return true
  if (value === 'requires-review') return acknowledged === true
  return false
}

// Deterministic serialization for fingerprints: object keys sorted, arrays in
// order, so identical normalized inputs always produce identical text.
export function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const keys = Object.keys(value).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

// FNV-1a 32-bit over the stable serialization. Not cryptographic — it exists
// so a proposal records which inputs produced it and a reviewer can tell two
// runs apart at a glance.
export function computeInputFingerprint(value) {
  const text = stableStringify(value)
  let hash = 0x811c9dc5
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}
